import { cn } from "@/lib/utils";

interface CssOrbProps {
  /** Diameter in px (responsive cap applied by the parent container). */
  size?: number;
  /** Fill the parent element instead of using a fixed pixel size. */
  fill?: boolean;
  className?: string;
}

/**
 * Pure-CSS orb: warm coral-to-cream sphere with a top-light highlight,
 * a soft contact shadow and an ambient brand-coloured bloom.
 * No canvas, no images, no particles.
 */
export function CssOrb({ size = 220, fill = false, className }: CssOrbProps) {
  return (
    <span
      aria-hidden
      className={cn(fill ? "block h-full w-full" : "relative block", className)}
      style={fill ? undefined : { width: size, height: size }}
    >
      {/* Ambient outer bloom */}
      <span
        className="absolute rounded-full pointer-events-none orb-glow"
        style={{
          inset: fill ? "-28%" : -size * 0.28,
          background:
            "radial-gradient(circle, rgba(249,115,22,0.22) 0%, rgba(249,115,22,0.08) 45%, rgba(249,115,22,0) 72%)",
        }}
      />

      {/* Sphere */}
      <span
        className="absolute inset-0 rounded-full orb-breathe"
        style={{
          background:
            "radial-gradient(circle at 32% 26%, #FFF6EC 0%, #FFD9B0 26%, #FBA968 52%, #F97316 78%, #E4600B 100%)",
          boxShadow:
            "0 22px 44px -18px rgba(194,65,12,0.55), 0 0 60px rgba(249,115,22,0.28), inset 0 -18px 32px rgba(154,52,18,0.35), inset 0 10px 22px rgba(255,255,255,0.45)",
        }}
      >
        {/* Top-light highlight */}
        <span
          className="absolute rounded-full pointer-events-none"
          style={{
            top: "9%",
            left: "20%",
            width: "44%",
            height: "30%",
            background:
              "radial-gradient(closest-side, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0) 100%)",
            filter: "blur(2px)",
          }}
        />
        {/* Bottom bounce shadow */}
        <span
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 118%, rgba(120,40,8,0.42) 0%, rgba(120,40,8,0) 55%)",
          }}
        />
      </span>
    </span>
  );
}
